/**
 * useOnboarding Hook
 *
 * Manages onboarding state, API key creation, and completion logic.
 *
 * @module hooks/useOnboarding
 */

import { useState, useCallback, useEffect } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { API_URL } from '@/lib/config';
import { useAuth } from '@/contexts/AuthContext';

// =============================================================================
// Constants
// =============================================================================

/**
 * Test IP address for local development.
 * In development, the client IP is localhost which can't be geo-located.
 * We use Google's public DNS as a reliable test IP.
 */
const DEV_TEST_IP = '8.8.8.8';

/**
 * Check if running in development mode
 */
const isDevelopment = import.meta.env.DEV;

// =============================================================================
// Types
// =============================================================================

export interface OnboardingState {
  /** Current step (1-5) */
  currentStep: number;
  /** Steps that have been completed */
  completedSteps: Set<number>;
  /** Whether profile has been saved */
  profileSaved: boolean;
  /** The created API key (full key, only shown once) */
  createdApiKey: string | null;
  /** API test result */
  testResult: GeoLookupResponse | null;
  /** Loading states */
  loading: {
    profile: boolean;
    apiKey: boolean;
    test: boolean;
  };
  /** Error states */
  errors: {
    profile: string | null;
    apiKey: string | null;
    test: string | null;
  };
}

export interface ProfileData {
  firstName: string;
  lastName: string;
  organizationName?: string;
}

export interface GeoLookupResponse {
  success: boolean;
  data?: {
    ip: string;
    ip_type: string;
    location: {
      continent_code: string;
      continent_name: string;
      country_code: string;
      country_code_iso3: string;
      country_name: string;
      country_capital: string;
      country_tld: string;
      country_calling_code: string;
      country_flag_emoji: string;
      country_languages: string[];
      country_area_km2: number;
      country_population: number;
      country_neighbors: string[];
      country_is_eu: boolean;
      region_code: string;
      region_name: string;
      city_name: string;
      postal_code: string;
      latitude: number;
      longitude: number;
      weather_code: string;
    };
    time: {
      zone: string;
      abbr: string;
      utc_offset: string;
      utc_offset_seconds: number;
      current_time: string;
      current_timestamp: number;
      is_dst: boolean;
    };
    currency: {
      code: string;
      name: string;
      symbol: string;
    };
    network: {
      asn: number;
      asn_organization: string;
      isp: string;
      organization: string;
      connection_type: string;
    };
  };
  /** Error flag for failed responses */
  error?: boolean;
  /** Error code for failed responses */
  error_code?: number;
  /** Error message for failed responses */
  error_message?: string;
}

interface CreateApiKeyResponse {
  success: boolean;
  key?: string;
  keyId?: string;
  error?: string;
}

export interface UseOnboardingReturn extends OnboardingState {
  /** Save profile and create API key */
  saveProfileAndCreateKey: (data: ProfileData) => Promise<void>;
  /** Test the created API key */
  testApiKey: () => Promise<void>;
  /** Mark a step as complete */
  completeStep: (step: number) => void;
  /** Go to a specific step */
  goToStep: (step: number) => void;
  /** Check if a step is accessible */
  isStepAccessible: (step: number) => boolean;
  /** Mark onboarding as complete (call when user finishes all steps) */
  completeOnboarding: () => Promise<void>;
}

// =============================================================================
// Session Storage Keys
// =============================================================================

const STORAGE_KEYS = {
  API_KEY: 'hb_onboarding_api_key',
  COMPLETED_STEPS: 'hb_onboarding_completed_steps',
  CURRENT_STEP: 'hb_onboarding_current_step',
} as const;

// =============================================================================
// Hook
// =============================================================================

export function useOnboarding(): UseOnboardingReturn {
  const { user, firebaseUser, organization, refreshOrganization } = useAuth();

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const [currentStep, setCurrentStep] = useState<number>(() => {
    const stored = sessionStorage.getItem(STORAGE_KEYS.CURRENT_STEP);
    return stored ? parseInt(stored, 10) : 1;
  });

  const [completedSteps, setCompletedSteps] = useState<Set<number>>(() => {
    const stored = sessionStorage.getItem(STORAGE_KEYS.COMPLETED_STEPS);
    if (stored) {
      try {
        return new Set(JSON.parse(stored));
      } catch {
        return new Set();
      }
    }
    return new Set();
  });

  const [profileSaved, setProfileSaved] = useState(false);

  const [createdApiKey, setCreatedApiKey] = useState<string | null>(() => {
    return sessionStorage.getItem(STORAGE_KEYS.API_KEY);
  });

  const [testResult, setTestResult] = useState<GeoLookupResponse | null>(null);

  const [loading, setLoading] = useState({
    profile: false,
    apiKey: false,
    test: false,
  });

  const [errors, setErrors] = useState({
    profile: null as string | null,
    apiKey: null as string | null,
    test: null as string | null,
  });

  // ---------------------------------------------------------------------------
  // Persist state to session storage
  // ---------------------------------------------------------------------------

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEYS.CURRENT_STEP, currentStep.toString());
  }, [currentStep]);

  useEffect(() => {
    sessionStorage.setItem(
      STORAGE_KEYS.COMPLETED_STEPS,
      JSON.stringify(Array.from(completedSteps))
    );
  }, [completedSteps]);

  useEffect(() => {
    if (createdApiKey) {
      sessionStorage.setItem(STORAGE_KEYS.API_KEY, createdApiKey);
    }
  }, [createdApiKey]);

  // ---------------------------------------------------------------------------
  // Check if user already has profile data
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (user?.firstName && user?.lastName) {
      setProfileSaved(true);
      // If they have a profile but no API key stored, they need to start at step 1
      // But if they have an API key, they can continue from where they left off
      if (!createdApiKey) {
        // User has profile but might need to create key
        // Keep them at current step
      }
    }
  }, [user, createdApiKey]);

  // ---------------------------------------------------------------------------
  // Save Profile and Create API Key
  // ---------------------------------------------------------------------------

  const saveProfileAndCreateKey = useCallback(
    async (data: ProfileData) => {
      if (!user || !firebaseUser || !organization) {
        setErrors((prev) => ({
          ...prev,
          profile: 'User or organization not found.',
        }));
        return;
      }

      setLoading((prev) => ({ ...prev, profile: true, apiKey: true }));
      setErrors((prev) => ({ ...prev, profile: null, apiKey: null }));

      try {
        // 1. Update user profile
        const combinedDisplayName = [data.firstName.trim(), data.lastName.trim()]
          .filter(Boolean)
          .join(' ') || null;

        await updateDoc(doc(db, 'users', user.id), {
          firstName: data.firstName.trim(),
          lastName: data.lastName.trim(),
          displayName: combinedDisplayName,
          updatedAt: serverTimestamp(),
        });

        // Update Firebase Auth profile
        await updateProfile(firebaseUser, {
          displayName: combinedDisplayName,
        });

        // 2. Update member document with display name
        await updateDoc(doc(db, 'organizations', organization.id, 'members', user.id), {
          displayName: combinedDisplayName,
        });

        // 3. Update organization name if provided
        const orgName =
          data.organizationName?.trim() ||
          `${data.firstName.trim()}'s Organization`;

        await updateDoc(doc(db, 'organizations', organization.id), {
          name: orgName,
          updatedAt: serverTimestamp(),
        });

        // 4. Update orgMemberships in user document with new org name
        // This keeps the denormalized data in sync for the org switcher
        const updatedMemberships = (user.orgMemberships || []).map((m) =>
          m.orgId === organization.id ? { ...m, orgName } : m
        );
        await updateDoc(doc(db, 'users', user.id), {
          orgMemberships: updatedMemberships,
        });

        // Refresh the organization data in AuthContext so UI shows updated name
        await refreshOrganization();

        setProfileSaved(true);
        setLoading((prev) => ({ ...prev, profile: false }));

        // 4. Create API key (only if not already created)
        if (!createdApiKey) {
          const createApiKey = httpsCallable<
            { label: string; type: string; orgId: string },
            CreateApiKeyResponse
          >(functions, 'createApiKey');

          const result = await createApiKey({
            label: 'My First Key',
            type: 'live',
            orgId: organization.id,
          });

          if (!result.data.success) {
            throw new Error(result.data.error || 'Failed to create API key.');
          }

          if (!result.data.key) {
            throw new Error('No key returned from server.');
          }

          setCreatedApiKey(result.data.key);
        }

        setLoading((prev) => ({ ...prev, apiKey: false }));

        // Complete step 1 and move to step 2
        setCompletedSteps((prev) => new Set([...prev, 1]));
        setCurrentStep(2);
      } catch (err) {
        console.error('Error during profile/key creation:', err);
        const error = err as Error;
        setErrors((prev) => ({
          ...prev,
          profile: error.message || 'Failed to save profile.',
          apiKey: error.message || 'Failed to create API key.',
        }));
      } finally {
        setLoading((prev) => ({ ...prev, profile: false, apiKey: false }));
      }
    },
    [user, firebaseUser, organization, createdApiKey, refreshOrganization]
  );

  // ---------------------------------------------------------------------------
  // Test API Key
  // ---------------------------------------------------------------------------

  const testApiKey = useCallback(async () => {
    if (!createdApiKey || !user) {
      setErrors((prev) => ({
        ...prev,
        test: 'No API key available to test.',
      }));
      return;
    }

    setLoading((prev) => ({ ...prev, test: true }));
    setErrors((prev) => ({ ...prev, test: null }));
    setTestResult(null);

    try {
      // In development, use a test IP since localhost can't be geo-located
      const lookupUrl = isDevelopment
        ? `${API_URL}/v1/geo/lookup?ip=${DEV_TEST_IP}`
        : `${API_URL}/v1/geo/lookup`;

      const response = await fetch(lookupUrl, {
        headers: {
          'X-API-Key': createdApiKey,
        },
      });

      const data: GeoLookupResponse = await response.json();
      setTestResult(data);

      if (data.success) {
        // Mark step 3 as complete but stay on step 3
        // User clicks Continue to advance to step 4
        setCompletedSteps((prev) => new Set([...prev, 2, 3]));
      } else {
        setErrors((prev) => ({
          ...prev,
          test: data.error_message || 'API request failed.',
        }));
      }
    } catch (err) {
      console.error('Error testing API key:', err);
      setErrors((prev) => ({
        ...prev,
        test: 'Failed to reach the API. Please check your connection.',
      }));
    } finally {
      setLoading((prev) => ({ ...prev, test: false }));
    }
  }, [createdApiKey, user]);

  // ---------------------------------------------------------------------------
  // Step Navigation
  // ---------------------------------------------------------------------------

  const completeStep = useCallback((step: number) => {
    setCompletedSteps((prev) => new Set([...prev, step]));
  }, []);

  const goToStep = useCallback((step: number) => {
    setCurrentStep(step);
  }, []);

  const isStepAccessible = useCallback(
    (step: number) => {
      // Step 1 is always accessible
      if (step === 1) return true;

      // Steps 2-3 require profile saved and API key created
      if (step >= 2 && step <= 3) {
        return profileSaved && !!createdApiKey;
      }

      // Steps 4-5 require successful API test
      if (step >= 4) {
        return completedSteps.has(3);
      }

      return false;
    },
    [profileSaved, createdApiKey, completedSteps]
  );

  // ---------------------------------------------------------------------------
  // Complete Onboarding
  // ---------------------------------------------------------------------------

  const completeOnboarding = useCallback(async () => {
    if (!user) return;

    try {
      await updateDoc(doc(db, 'users', user.id), {
        onboardingCompletedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Clear session storage
      sessionStorage.removeItem(STORAGE_KEYS.API_KEY);
      sessionStorage.removeItem(STORAGE_KEYS.COMPLETED_STEPS);
      sessionStorage.removeItem(STORAGE_KEYS.CURRENT_STEP);
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
    }
  }, [user]);

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    currentStep,
    completedSteps,
    profileSaved,
    createdApiKey,
    testResult,
    loading,
    errors,
    saveProfileAndCreateKey,
    testApiKey,
    completeStep,
    goToStep,
    isStepAccessible,
    completeOnboarding,
  };
}
